import React, { useState, useEffect, useCallback } from "react";
import { IMedia, IMediaRating } from "../../../types";
import { Clock, StarFill, PlayFill, XLg } from "react-bootstrap-icons";
import { StarRating } from "../../star-rating";
import { colors } from "../../../libs/themes";
import { durationFormatter } from "../../../libs/utils";
import * as Styled from "./MobileDetailModal.styled";
import { useTheme } from "styled-components";

const CLOSE_ANIMATION_MS = 250;

interface MobileDetailModalProps {
    isOpen: boolean;
    onClose: () => void;
    mediaItem: IMedia;
    mediaRating?: IMediaRating;
    enableRatingControls: boolean;
    onWatchClick: (mediaItem: IMedia) => void;
    onRatingSuccess: () => void;
    isSignedIn: boolean;
    hasEligiblePlan: boolean;
    sessionToken: string;
    enableCompetitionMode?: boolean;
}

export const MobileDetailModal: React.FC<MobileDetailModalProps> = ({
    isOpen,
    onClose,
    mediaItem,
    mediaRating,
    enableRatingControls,
    onWatchClick,
    onRatingSuccess,
    isSignedIn,
    hasEligiblePlan,
    sessionToken,
    enableCompetitionMode,
}) => {
    const LABEL_WATCH_NOW = "Watch Now";
    const LABEL_GET_ACCESS = "Purchase Plan";
    const LABEL_PURCHASE_PLAN = "Purchase Plan";

    const [isClosing, setIsClosing] = useState(false);
    const [shouldRender, setShouldRender] = useState(isOpen);
    const theme = useTheme();

    useEffect(() => {
        if (isOpen) {
            setShouldRender(true);
            setIsClosing(false);
        }
    }, [isOpen]);

    const handleClose = useCallback(() => {
        setIsClosing(true);
        setTimeout(() => {
            setShouldRender(false);
            setIsClosing(false);
            onClose();
        }, CLOSE_ANIMATION_MS);
    }, [onClose]);

    if (!shouldRender) return null;

    const shouldShowRating = !enableCompetitionMode;
    const canRate = enableRatingControls && isSignedIn && hasEligiblePlan;
    const matchedRating =
        mediaRating?.mediaId === mediaItem?.mediaId ? mediaRating : undefined;

    const buttonLabel = !!mediaItem.planDetails?.length
        ? isSignedIn
            ? hasEligiblePlan
                ? LABEL_WATCH_NOW
                : LABEL_PURCHASE_PLAN
            : LABEL_GET_ACCESS
        : LABEL_WATCH_NOW;

    return (
        <Styled.Overlay onClick={handleClose} $isClosing={isClosing}>
            <Styled.ModalContainer onClick={(e) => e.stopPropagation()} $isClosing={isClosing}>
                <Styled.CloseButton onClick={handleClose}>
                    <XLg size={14} />
                </Styled.CloseButton>

                <Styled.ThumbnailWrapper>
                    <img src={mediaItem.thumbnail} alt={mediaItem.title} />
                </Styled.ThumbnailWrapper>

                <Styled.ContentWrapper>
                    <Styled.TrailerSection>
                        <Styled.TrailerButton onClick={() => onWatchClick(mediaItem)}>
                            {buttonLabel}
                            <PlayFill color={theme?.cardBg ?? colors.text.black} size={20} />
                        </Styled.TrailerButton>
                    </Styled.TrailerSection>

                    {mediaItem.title && (
                        <Styled.Title>{mediaItem.title}</Styled.Title>
                    )}

                    {mediaItem.description && (
                        <Styled.Description title={mediaItem.description}>
                            {mediaItem.description}
                        </Styled.Description>
                    )}

                    {mediaItem.genre && (
                        <Styled.GenrePills>
                            <Styled.Genre>{mediaItem.genre}</Styled.Genre>
                        </Styled.GenrePills>
                    )}

                    {(mediaItem.duration || (shouldShowRating && matchedRating)) && (
                        <Styled.InfoRow>
                            {mediaItem.duration && (
                                <Styled.InfoItem>
                                    <Clock color={theme?.cardText ?? colors.text.black} />
                                    <div>{durationFormatter(mediaItem.duration)}</div>
                                </Styled.InfoItem>
                            )}
                            {shouldShowRating && matchedRating && (
                                <Styled.InfoItem>
                                    <StarFill color={`${colors.text.black}`} size={16} />
                                    <span>{matchedRating?.rating ?? 0} /5</span>
                                </Styled.InfoItem>
                            )}
                        </Styled.InfoRow>
                    )}

                    {canRate && (
                        <Styled.RatingWrapper>
                            <StarRating
                                mediaItem={mediaItem}
                                mediaRating={mediaRating?.rating ?? 0}
                                sessionToken={sessionToken}
                                onRatingSuccess={onRatingSuccess}
                            />
                        </Styled.RatingWrapper>
                    )}

                    {((mediaItem.year && mediaItem.year.length > 0) ||
                        (mediaItem.duration && mediaItem.duration.length > 0) ||
                        (mediaItem.audience && mediaItem.audience.length > 0)) && (
                        <Styled.MetadataRow>
                            {mediaItem.year && mediaItem.year.length > 0 && (
                                <span className="release-year">{mediaItem.year}</span>
                            )}
                            {mediaItem.duration && mediaItem.duration.length > 0 && (
                                <span className="duration">
                                    {durationFormatter(mediaItem.duration)}
                                </span>
                            )}
                            {mediaItem.audience && mediaItem.audience.length > 0 && (
                                <span className="mpaa-rating">
                                    <span>{mediaItem.audience}</span>
                                </span>
                            )}
                            {mediaItem.viewerRating &&
                                mediaItem.audience &&
                                mediaItem.audience.length > 0 && (
                                    <span className="viewer-rating">
                                        <span className="viewer-rating--filled">
                                            &#9733;&#9733;&#9733;
                                        </span>
                                        <span className="viewer-rating--empty">
                                            &#9734;&#9734;
                                        </span>
                                    </span>
                                )}
                        </Styled.MetadataRow>
                    )}

                    {((mediaItem.directors && mediaItem.directors.length > 0) ||
                        (mediaItem.producers && mediaItem.producers.length > 0)) && (
                        <Styled.CrewSection>
                            {mediaItem.directors && mediaItem.directors.length > 0 && (
                                <Styled.CrewItem>
                                    <span>Directed by:</span>{" "}
                                    {mediaItem.directors.replace(/,(?=[^\s])/g, ", ")}
                                </Styled.CrewItem>
                            )}
                            {mediaItem.producers && mediaItem.producers.length > 0 && (
                                <Styled.CrewItem>
                                    <span>Produced by:</span>{" "}
                                    {mediaItem.producers.replace(/,(?=[^\s])/g, ", ")}
                                </Styled.CrewItem>
                            )}
                        </Styled.CrewSection>
                    )}
                </Styled.ContentWrapper>
            </Styled.ModalContainer>
        </Styled.Overlay>
    );
};
